import swal from "sweetalert";
import axios from "../helpers/axios";
import { officeOrderConstants } from "./constants";

const getErrorMessage = (error, fallback = "Ошибка") => {
    return (
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        fallback
    );
};

const buildQuery = (params = {}) => {
    const query = Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== null && value !== "")
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join("&");

    return query ? `?${query}` : "";
};

const normalizeSort = (sort) => {
    if (!sort || sort === "AllTime") return undefined;
    return sort;
};

const normalizeMode = (mode) => {
    if (["office", "personal", "shared", "employees"].includes(mode)) return mode;
    return "office";
};

const buildOfficeBody = (payload = {}, officeId) => ({
    ...payload,
    officeId,
    office: officeId,
});

const pickOrderFromResponse = (data = {}) => {
    return data.order || data.orderDetails || data.result || data.orders || null;
};

export const searchOfficeManualProducts = ({
    q = "",
    officeId,
    source = "all",
    limit = 30,
} = {}) => {
    return async () => {
        try {
            const params = {
                q,
                office: officeId,
                officeId,
                source,
                limit,
            };

            const res = await axios.get(`/office/manualOrder/products${buildQuery(params)}`);

            if (res.status === 200) {
                return res.data?.products || [];
            }

            return [];
        } catch (error) {
            return [];
        }
    };
};

export const getOfficeColorProducts = ({ officeId } = {}) => {
    return async (dispatch) => {
        try {
            dispatch({ type: officeOrderConstants.OFFICE_ORDER_COLORS_REQUEST });

            const params = {
                office: officeId,
                officeId,
            };

            const res = await axios.get(`/office/color-products${buildQuery(params)}`);

            if (res.status === 200) {
                dispatch({
                    type: officeOrderConstants.OFFICE_ORDER_COLORS_SUCCESS,
                    payload: {
                        colorProducts: res.data?.colorProducts || [],
                    },
                });

                return res.data?.colorProducts || [];
            }

            dispatch({
                type: officeOrderConstants.OFFICE_ORDER_COLORS_FAILURE,
                payload: res.data || { error: "Ошибка загрузки цветов" },
            });

            return [];
        } catch (error) {
            dispatch({
                type: officeOrderConstants.OFFICE_ORDER_COLORS_FAILURE,
                payload: {
                    error: getErrorMessage(error, "Ошибка загрузки цветов"),
                },
            });

            return [];
        }
    };
};

export const setOfficeOrderMode = (mode) => ({
    type: officeOrderConstants.OFFICE_ORDER_SET_MODE,
    payload: normalizeMode(mode),
});

export const closeOfficeOrderAlert = () => ({
    type: officeOrderConstants.OFFICE_ORDER_CLOSE_ALERT,
});

export const getOfficeOrders = ({
    sort,
    officeId,
    mode = "office",
    q = "",
    status = "",
    page,
    limit,
} = {}) => {
    return async (dispatch) => {
        try {
            const safeMode = normalizeMode(mode);

            dispatch({
                type: officeOrderConstants.OFFICE_ORDER_LIST_REQUEST,
                meta: { mode: safeMode },
            });

            const params = {
                office: officeId,
                officeId,
                sort: normalizeSort(sort),
                mode: safeMode,
                scope: safeMode,
                q,
                status,
                page,
                limit,
            };

            const res = await axios.get(`/office/orders${buildQuery(params)}`);

            if (res.status === 200) {
                dispatch({
                    type: officeOrderConstants.OFFICE_ORDER_LIST_SUCCESS,
                    payload: {
                        orders: res.data?.orders || [],
                        counts: res.data?.counts || {},
                        meta: res.data?.meta || { mode: safeMode, officeId },
                        total: res.data?.total || 0,
                        page: res.data?.page || 1,
                        limit: res.data?.limit || limit || 0,
                    },
                });

                return res.data?.orders || [];
            }

            dispatch({
                type: officeOrderConstants.OFFICE_ORDER_LIST_FAILURE,
                payload: res.data || { error: "Ошибка загрузки офисных заказов" },
            });

            return [];
        } catch (error) {
            dispatch({
                type: officeOrderConstants.OFFICE_ORDER_LIST_FAILURE,
                payload: { error: getErrorMessage(error, "Ошибка загрузки офисных заказов") },
            });

            return [];
        }
    };
};

export const searchOfficeOrders = ({
    q,
    officeId,
    mode = "office",
    sort,
    status = "",
    page,
    limit,
} = {}) => {
    return async (dispatch) => {
        try {
            const safeMode = normalizeMode(mode);
            const search = String(q || "").trim();

            dispatch({
                type: officeOrderConstants.OFFICE_ORDER_SEARCH_REQUEST,
                meta: { mode: safeMode, q: search },
            });

            if (!search) {
                return dispatch(getOfficeOrders({
                    sort,
                    officeId,
                    mode: safeMode,
                    status,
                    page,
                    limit,
                }));
            }

            const params = {
                office: officeId,
                officeId,
                mode: safeMode,
                scope: safeMode,
                sort: normalizeSort(sort),
                status,
                page,
                limit,
            };

            const res = await axios.get(`/office/orders/search/${encodeURIComponent(search)}${buildQuery(params)}`);

            if (res.status === 200) {
                dispatch({
                    type: officeOrderConstants.OFFICE_ORDER_SEARCH_SUCCESS,
                    payload: {
                        orders: res.data?.orders || [],
                        counts: res.data?.counts || {},
                        meta: res.data?.meta || { mode: safeMode, officeId },
                        total: res.data?.total || 0,
                        page: res.data?.page || 1,
                        limit: res.data?.limit || limit || 0,
                        message: res.data?.message || "",
                    },
                });

                return res.data?.orders || [];
            }

            dispatch({
                type: officeOrderConstants.OFFICE_ORDER_SEARCH_FAILURE,
                payload: res.data || { error: "Ошибка поиска офисных заказов" },
            });

            return [];
        } catch (error) {
            dispatch({
                type: officeOrderConstants.OFFICE_ORDER_SEARCH_FAILURE,
                payload: { error: getErrorMessage(error, "Ошибка поиска офисных заказов") },
            });

            return [];
        }
    };
};

export const getOfficeOrderById = ({ orderId, officeId } = {}) => {
    return async (dispatch) => {
        try {
            dispatch({ type: officeOrderConstants.OFFICE_ORDER_DETAILS_REQUEST });

            const params = {
                office: officeId,
                officeId,
            };

            const res = await axios.get(`/office/orders/${orderId}${buildQuery(params)}`);

            if (res.status === 200) {
                dispatch({
                    type: officeOrderConstants.OFFICE_ORDER_DETAILS_SUCCESS,
                    payload: {
                        order: res.data?.order || {},
                    },
                });

                return res.data?.order || null;
            }

            dispatch({
                type: officeOrderConstants.OFFICE_ORDER_DETAILS_FAILURE,
                payload: res.data || { error: "Ошибка загрузки заказа" },
            });

            return null;
        } catch (error) {
            dispatch({
                type: officeOrderConstants.OFFICE_ORDER_DETAILS_FAILURE,
                payload: { error: getErrorMessage(error, "Ошибка загрузки заказа") },
            });

            return null;
        }
    };
};

export const addOfficeOrder = (payload, officeId) => {
    return async (dispatch) => {
        try {
            dispatch({ type: officeOrderConstants.OFFICE_ORDER_CREATE_REQUEST });

            const res = await axios.post("/office/addOrder", buildOfficeBody({
                ...payload,
                scope: "office",
            }, officeId));

            if (res.status === 201) {
                dispatch({
                    type: officeOrderConstants.OFFICE_ORDER_CREATE_SUCCESS,
                    payload: res.data,
                });

                swal({
                    title: "Офисный заказ успешно оформлен",
                    icon: "success",
                });

                return res.data;
            }

            dispatch({
                type: officeOrderConstants.OFFICE_ORDER_CREATE_FAILURE,
                payload: res.data,
            });

            return null;
        } catch (error) {
            dispatch({
                type: officeOrderConstants.OFFICE_ORDER_CREATE_FAILURE,
                payload: { error: getErrorMessage(error, "Ошибка оформления офисного заказа") },
            });

            return null;
        }
    };
};

export const addOfficeOrderWithScreens = (formData, officeId) => {
    return async (dispatch) => {
        try {
            dispatch({ type: officeOrderConstants.OFFICE_ORDER_CREATE_REQUEST });

            const rawPayload = formData.get("payload");

            if (rawPayload) {
                let parsedPayload = {};

                try {
                    parsedPayload =
                        typeof rawPayload === "string"
                            ? JSON.parse(rawPayload)
                            : JSON.parse(String(rawPayload));
                } catch (e) {
                    parsedPayload = {};
                }

                const nextPayload = {
                    ...parsedPayload,
                    scope: "office",
                    officeId,
                    office: officeId,
                };

                formData.set("payload", JSON.stringify(nextPayload));
            }

            const res = await axios.post("/office/addOrderWithScreens", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            if (res.status === 201) {
                dispatch({
                    type: officeOrderConstants.OFFICE_ORDER_CREATE_SUCCESS,
                    payload: res.data,
                });

                swal({
                    title: "Офисный заказ успешно оформлен",
                    icon: "success",
                });

                return res.data;
            }

            dispatch({
                type: officeOrderConstants.OFFICE_ORDER_CREATE_FAILURE,
                payload: res.data,
            });

            return null;
        } catch (error) {
            dispatch({
                type: officeOrderConstants.OFFICE_ORDER_CREATE_FAILURE,
                payload: {
                    error: getErrorMessage(error, "Ошибка оформления офисного заказа"),
                },
            });

            return null;
        }
    };
};

const runOfficeOrderMutation = ({
    url,
    body,
    officeId,
    successMessage = "Заказ обновлён",
    reload,
    reloadParams,
}) => {
    return async (dispatch) => {
        try {
            dispatch({ type: officeOrderConstants.OFFICE_ORDER_MUTATION_REQUEST });

            const res = await axios.post(url, buildOfficeBody(body, officeId));

            if (res.status === 200 || res.status === 201 || res.status === 202) {
                const order = pickOrderFromResponse(res.data);

                dispatch({
                    type: officeOrderConstants.OFFICE_ORDER_MUTATION_SUCCESS,
                    payload: {
                        ...res.data,
                        order,
                        message: res.data?.message || successMessage,
                    },
                });

                if (reload) {
                    dispatch(getOfficeOrders({
                        officeId,
                        ...(reloadParams || {}),
                    }));
                }

                return {
                    ok: true,
                    order,
                    data: res.data,
                    message: res.data?.message || successMessage,
                };
            }

            dispatch({
                type: officeOrderConstants.OFFICE_ORDER_MUTATION_FAILURE,
                payload: res.data || { error: successMessage },
            });

            return {
                ok: false,
                error: res.data?.error || res.data?.message || successMessage,
            };
        } catch (error) {
            const message = getErrorMessage(error, successMessage);

            dispatch({
                type: officeOrderConstants.OFFICE_ORDER_MUTATION_FAILURE,
                payload: { error: message },
            });

            return {
                ok: false,
                error: message,
            };
        }
    };
};

export const updateOfficeOrderStatus = ({ orderId, type, officeId }) => {
    return runOfficeOrderMutation({
        url: "/office/order/update-status",
        officeId,
        body: { orderId, type },
        successMessage: "Статус заказа обновлён",
    });
};

export const updateOfficeOrderTotal = ({ orderId, totalAmount, officeId }) => {
    return runOfficeOrderMutation({
        url: "/office/order/update-total",
        officeId,
        body: {
            orderId,
            totalAmount,
            typeSet: "totalAmount",
        },
        successMessage: "Сумма заказа обновлена",
    });
};

export const updateOfficeOrderNumber = ({
    orderId,
    orderNumber,
    orderNumberAdditional,
    officeId,
}) => {
    return runOfficeOrderMutation({
        url: "/office/order/update-number",
        officeId,
        body: {
            orderId,
            orderNumber,
            orderNumberAdditional,
            typeSet: "orderNumberAdditional",
        },
        successMessage: "Номер заказа обновлён",
    });
};

export const updateOfficeOrderTracking = ({ orderId, packageTracking, officeId }) => {
    return runOfficeOrderMutation({
        url: "/office/order/update-tracking",
        officeId,
        body: { orderId, packageTracking },
        successMessage: "ТТН обновлён",
    });
};

export const updateOfficeOrderDiscount = ({
    orderId,
    discountForBuyers,
    discountedAmount,
    officeId,
}) => {
    return runOfficeOrderMutation({
        url: "/office/order/update-discount",
        officeId,
        body: {
            orderId,
            discountForBuyers,
            discountedAmount,
        },
        successMessage: "Скидка обновлена",
    });
};

export const toggleOfficeOrderDiscount = ({ orderId, officeId }) => {
    return runOfficeOrderMutation({
        url: "/office/order/toggle-discount",
        officeId,
        body: { orderId },
        successMessage: "Видимость скидки обновлена",
    });
};

export const toggleOfficeOrderVisible = ({ orderId, officeId, reloadParams } = {}) => {
    return runOfficeOrderMutation({
        url: "/office/order/toggle-visible",
        officeId,
        body: { orderId },
        successMessage: "Видимость заказа обновлена",
        reload: true,
        reloadParams,
    });
};

export const updateOfficeOrderDeliveryPayment = ({
    orderId,
    deliveryId,
    pickupPointsId,
    paymentId,
    officeId,
}) => {
    return runOfficeOrderMutation({
        url: "/office/order/update-delivery-payment",
        officeId,
        body: {
            orderId,
            deliveryId,
            pickupPointsId,
            paymentId,
        },
        successMessage: "Доставка и оплата обновлены",
    });
};

export const addOfficeOrderProducts = ({ orderId, productIds, officeId }) => {
    return runOfficeOrderMutation({
        url: "/office/order/add-products",
        officeId,
        body: {
            orderId,
            productIds,
        },
        successMessage: "Товары добавлены в заказ",
    });
};

export const deleteOfficeOrderProduct = ({
    orderId,
    productOrderDetailsId,
    fullNameProduct,
    officeId,
}) => {
    return runOfficeOrderMutation({
        url: "/office/order/delete-product",
        officeId,
        body: {
            orderId,
            productOrderDetailsId,
            fullNameProduct,
        },
        successMessage: "Товар удалён из заказа",
    });
};

export const updateOfficeOrderProduct = ({
    orderId,
    productOrderDetailsId,
    length,
    width,
    height,
    typeProductColor,
    purchasedQty,
    bayerPrice,
    officeId,
}) => {
    return runOfficeOrderMutation({
        url: "/office/order/update-product",
        officeId,
        body: {
            orderId,
            productOrderDetailsId,
            length,
            width,
            height,
            typeProductColor,
            purchasedQty,
            bayerPrice,
        },
        successMessage: "Позиция заказа обновлена",
    });
};

export const addOfficeOrderAdditionalField = ({
    orderId,
    productOrderDetailsId,
    afTitle,
    afDescription,
    afViewType,
    officeId,
}) => {
    return runOfficeOrderMutation({
        url: "/office/order/add-additional-field",
        officeId,
        body: {
            orderId,
            productOrderDetailsId,
            afTitle,
            afDescription,
            afViewType,
        },
        successMessage: "Дополнительное поле добавлено",
    });
};

export const updateOfficeOrderAdditionalField = ({
    orderId,
    productOrderDetailsId,
    additionalId,
    afTitle,
    afDescription,
    afViewType,
    officeId,
}) => {
    return runOfficeOrderMutation({
        url: "/office/order/update-additional-field",
        officeId,
        body: {
            orderId,
            productOrderDetailsId,
            additionalId,
            afTitle,
            afDescription,
            afViewType,
        },
        successMessage: "Дополнительное поле обновлено",
    });
};

export const deleteOfficeOrderAdditionalField = ({
    orderId,
    additionalId,
    officeId,
}) => {
    return runOfficeOrderMutation({
        url: "/office/order/delete-additional-field",
        officeId,
        body: {
            orderId,
            additionalId,
        },
        successMessage: "Дополнительное поле удалено",
    });
};